import React, { useEffect, useRef, useState } from 'react';
import { Layout } from 'antd';
import { Helmet } from 'react-helmet';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '../../../components/Brand/Icon';
import WiwHeaderTitle from '../../../components/WiwHeaderTitle';
import ControlButton from '../../../components/WiwFooter/ControlButton';
import ButtonPanelReaction from '../../../components/Buttons/ButtonPanelReaction';
import usePageLayout from '../../../hooks/usePageLayout';
import useSessionIdentity, { getSessionIdentity } from '../../../hooks/useSessionIdentity';
import { safeAuthReturn } from '../../../guestTrial/store';
import { getUser, removeUser } from '../../../utils/func';
import { REPORT_TYPES } from '../../../utils/reportTypes';
import { APP_ROUTES } from '../../../utils/constant';
import url from '../../../utils/url';
import api from '../../../services';
import { isValidReportId, sendReactionReport, sendUserReport } from '../../../services/reports';
import './index.scss';

const { Header, Footer } = Layout;

export default function ReactionReport() {
  return <ReportTarget target="reaction" />;
}

export function UserReport() {
  return <ReportTarget target="user" />;
}

function profileReturnPath(username) {
  if (typeof username !== 'string' || !username.trim() || ['.', '..'].includes(username)) return url(APP_ROUTES.MAIN);
  try { return url(`${APP_ROUTES.USER}/${encodeURIComponent(username)}`); }
  catch { return url(APP_ROUTES.MAIN); }
}

function ReportTarget({ target }) {
  const { id } = useParams();
  const location = useLocation();
  const session = useSessionIdentity();
  const loginPath = url(`${APP_ROUTES.LOGIN}?from=${encodeURIComponent(location.pathname + location.search + location.hash)}`);
  if (!isValidReportId(id)) return <Navigate to={url(APP_ROUTES.PAGE404)} replace />;
  if (!getUser()) return <Navigate to={loginPath} replace />;
  const from = location.state?.from;
  const returnState = typeof from === 'string' && safeAuthReturn(from) === from ? { from } : undefined;
  return <ReportForm key={`${target}:${id}:${session}`} target={target} targetId={id} session={session}
    loginPath={loginPath} initialStoryId={location.state?.storyId}
    returnPath={target === 'user' ? profileReturnPath(location.state?.username) : url(`${APP_ROUTES.REACTION}/${id}`)}
    returnState={target === 'user' ? returnState : undefined} />;
}

function ReportForm({ target, targetId, session, loginPath, initialStoryId, returnPath, returnState }) {
  const { t } = useTranslation(['brand', 'blocking']);
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const feedbackRef = useRef(null);
  const mounted = useRef(false);
  const pending = useRef(false);
  const [choice, setChoice] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [complete, setComplete] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [storyId, setStoryId] = useState(isValidReportId(initialStoryId) ? initialStoryId : null);
  const { contentTop, contentHeight } = usePageLayout(headerRef, footerRef);
  const userTarget = target === 'user';
  const title = t(userTarget ? 'blocking:reportUserTitle' : 'reportTitle');
  const returnLabel = t(userTarget ? 'blocking:reportUserBack' : 'reportBack');
  const formId = `${target}-report-form`;
  const ownsSession = () => mounted.current && session === getSessionIdentity();

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (target !== 'reaction' || storyId) return;
    let active = true;
    // Context is optional; reporting itself does not depend on a successful read.
    api.reactionLoad({ id: targetId }).then(response => {
      if (!active || session !== getSessionIdentity()) return;
      if ([403, 404].includes(response?.statusCode)) {
        setUnavailable(true);
        setFeedback('reportUnavailable');
      } else if (response?.success) {
        const reaction = response.list?.find(item => String(item.id) === String(targetId));
        if (isValidReportId(reaction?.story?.id)) setStoryId(reaction.story.id);
      }
    }).catch(() => { /* A context lookup failure must not prevent a report. */ });
    return () => { active = false; };
  }, [target, targetId, storyId, session]);

  useEffect(() => {
    if (feedback) feedbackRef.current?.focus();
  }, [feedback]);

  const returnToTarget = () => navigate(returnPath, { replace: true, state: returnState });
  const submit = async (event) => {
    event.preventDefault();
    if (!choice || pending.current || complete || unavailable || !ownsSession()) return;
    pending.current = true;
    setSending(true);
    setFeedback('');
    try {
      const response = await (userTarget ? sendUserReport : sendReactionReport)(targetId, choice);
      if (!ownsSession()) return;
      if (response?.statusCode === 401) {
        removeUser();
        navigate(loginPath, { replace: true });
      } else if ([403, 404].includes(response?.statusCode)) {
        setUnavailable(true);
        setFeedback(userTarget ? 'blocking:reportUserUnavailable' : 'reportUnavailable');
      } else if (response?.success) {
        setComplete(true);
        setFeedback(response.already_reported ? 'reportDuplicate' : 'reportSuccess');
      } else {
        setFeedback('reportError');
      }
    } catch {
      if (ownsSession()) setFeedback('reportError');
    } finally {
      if (ownsSession()) {
        pending.current = false;
        setSending(false);
      }
    }
  };

  return (
    <Layout className="app-layout reaction-report">
      <Helmet><title>{title}</title></Helmet>
      <Header className="header" ref={headerRef}>
        <WiwHeaderTitle title={title} onBack={returnToTarget} backLabel={userTarget ? returnLabel : t('back')} />
      </Header>
      <main className="report-main" style={{ top: contentTop, height: contentHeight }}>
        {feedback && <p ref={feedbackRef} tabIndex={-1} className="report-feedback"
          role={complete ? 'status' : 'alert'}>{t(feedback)}</p>}
        {!complete && !unavailable && <form id={formId} onSubmit={submit} aria-label={title} aria-busy={sending}>
          <fieldset disabled={sending} className="report-reasons" aria-label={t('reportProblem')}>
            {REPORT_TYPES.map(type => <label key={type} className={`report-reason${choice === type ? ' is-selected' : ''}`}>
              <input type="radio" name="report-reason" value={type} checked={choice === type}
                onChange={() => setChoice(type)} />
              <span className="report-reason-text">{t(`reportReasons.${type}`)}</span>
              <span className="report-reason-marker" aria-hidden="true">{choice === type && <CheckIcon size={16} />}</span>
            </label>)}
          </fieldset>
        </form>}
        {complete && <button type="button" className="report-return" onClick={returnToTarget}>{returnLabel}</button>}
        {unavailable && <button type="button" className="report-return" onClick={() => navigate(url(APP_ROUTES.MAIN), { replace: true })}>{t('home')}</button>}
      </main>
      <Footer ref={footerRef}>
        {!complete && !unavailable && <div className="report-confirm">
          <p className="confirmation-question">{sending ? t('reportSending') : t('reportQuestion')}</p>
          <ControlButton action="send" form={formId} disabledOk={!choice || sending} busy={sending}
            disabledCancel={false} onCancel={returnToTarget} cancelLabel={t('cancel')}
            confirmLabel={t('reportSend')} busyLabel={t('reportSending')} className="report-confirm-buttons"
            cancelClassName="report-cancel" confirmClassName="report-send" />
        </div>}
        <ButtonPanelReaction className="report-navigation" />
      </Footer>
    </Layout>
  );
}
